import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import cn from 'classnames'
import styles from './VNCategorySearch.module.scss'
import { ButtonBase } from '@material-ui/core'
import { ReactComponent as CloseIcon } from '../../assets/icons/Close-Icon.svg'
import { ReactComponent as InputSearchIcon } from '../../assets/input-search-icon.svg'
import { onMountScanScreen } from '../../utils/scanScreen'
import { isE700Device } from '../../VNAndroidSDK/Utils'

const DEBOUNCE_TIME = 175

/**
 * VNSearch component allows users to search for items within current category
 *
 * @returns {JSX.Element}
 */
const VNSearch = ({
  hideSearch = () => {},
  emptySearchPlaceholder = '',
  searchInProgressPlaceholder = '',
  setParentValue = () => {},
  minQueryLength = 2,
  searchRef = null,
  setSearchActive = () => {},
}) => {
  const dispatch = useDispatch()
  const debounceTimer = useRef(null)
  const wrapperRef = useRef(null)
  const quickSearchRef = useRef(null)
  const [value, setValue] = useState('')
  const isMobile = useMemo(() => window.matchMedia('(max-width: 768px)').matches, [])
  const shouldDisplayClose = useMemo(() => !isMobile || value.length > 0, [isMobile, value])

  useImperativeHandle(searchRef, () => ({
    hideKeyboard: () => {
      quickSearchRef.current && quickSearchRef.current.blur()
    },
  }))

  // close search and reset input
  const closeSearch = useCallback(() => {
    setValue('')
    hideSearch()
    setParentValue('')

    if (isMobile) {
      quickSearchRef.current.blur()
      return
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, hideSearch, isMobile])

  // clear input and hide search
  const clearInput = useCallback(
    (cancelSearch = true) => {
      setValue('')
      setParentValue('')

      if (cancelSearch) {
        quickSearchRef.current.blur()
        hideSearch()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hideSearch],
  )

  const handleFocusChange = () => {
    setTimeout(() => {
      setSearchActive(document.activeElement === quickSearchRef?.current)
    }, 150)
  }

  useEffect(() => {
    return onMountScanScreen(handleFocusChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // handle input changes and debounce search
  useEffect(() => {
    if (debounceTimer?.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // has minimum search length
      setParentValue(value)
    }, DEBOUNCE_TIME)

    return () => clearTimeout(debounceTimer?.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, dispatch, clearInput])

  // handle input changes
  const handleChange = (e) => {
    setValue(e.target.value)
  }
  const handleBlur = () => {
    // if (query.length) return
    // clearInput(true)
  }

  // on enter, blur the input to hide the keyboard
  const handleKeyDown = (e) => {
    if (e.key !== 'Enter') return

    quickSearchRef.current && quickSearchRef.current.blur()
  }

  // search input properties
  const inputProps = {
    name: 'search-input',
    className: styles.input,
    type: 'text',
    value: value,
    onBlur: handleBlur,
    autoComplete: 'off',
    ref: quickSearchRef,
    autoFocus: true,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
  }

  // mobile search
  if (isMobile) {
    return (
      <div className={cn(styles.wrapper, styles.reusableSearch)} ref={wrapperRef}>
        <div className={styles.container}>
          <div className={styles.magnifier}>
            <InputSearchIcon />
          </div>
          {!value.length && <div className={styles.placeholder}>{emptySearchPlaceholder}</div>}
          <input {...inputProps} />
          {!!value.length && (
            <ButtonBase disableRipple={isE700Device()} element="div" className={styles.close} onClick={closeSearch}>
              <CloseIcon />
            </ButtonBase>
          )}
        </div>
      </div>
    )
  }

  // tablet and desktop search
  return (
    <div className={cn(styles.wrapper, styles.animate, styles.reusableSearch)} ref={wrapperRef}>
      <div className={styles.container}>
        <div className={styles.magnifier}>
          <InputSearchIcon />
        </div>
        {!value.length && <div className={styles.placeholder}>{emptySearchPlaceholder}</div>}
        <input {...inputProps} />
        {!!value.length && <div className={styles.indicator}>{searchInProgressPlaceholder}</div>}
        {shouldDisplayClose && (
          <ButtonBase disableRipple={isE700Device()} element="div" className={styles.close} onClick={closeSearch}>
            <CloseIcon />
          </ButtonBase>
        )}
      </div>
    </div>
  )
}

export default VNSearch
